import { Platform } from '../types/Platform';
import { Color } from '../types/Color';
import { GamesRequestParams, getGamesUrl } from '../types/GamesRequestParams';
import { GameModel, GameMoveModel, GamesModel } from '../types/GamesModel';
import { assertResponse, getJson } from './Utils';
import { EvaluationResult } from '../types/EvaluationResult';
import { EvaluationStrength } from '../games-app/evaluationStrength';
import { FiltersModel } from '../types/FiltersModel';

async function getGames(params: GamesRequestParams): Promise<GamesModel> {
  const response = await fetch('/api/Games' + getGamesUrl(params));
  return await getJson<GamesModel>(response);
}

async function getById(id: number): Promise<GameModel> {
  const response = await fetch('/api/Games/byId?gameId=' + id);
  return await getJson<GameModel>(response);
}

async function updateUserGame(id: number, starred: boolean): Promise<void> {
  const response = await fetch(
    '/api/Games/updateUserGame?gameId=' + id + '&starred=' + starred,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  );
  await assertResponse(response);
}

async function getFilters(params: GamesRequestParams): Promise<FiltersModel> {
  const response = await fetch('/api/Games/filters' + getGamesUrl(params));
  return await getJson<FiltersModel>(response);
}

async function getAlternatives(
  pgn: string,
  continuations: boolean,
  username: string | null,
  color: Color | null,
  platform: Platform | null,
  timeControl: string | null,
  from: string | null,
  to: string | null,
  group: string | null,
): Promise<GameMoveModel[]> {
  const url =
    '/api/Games/alternatives?pgn=' +
    encodeURIComponent(pgn) +
    (continuations ? '&continuations=true' : '') +
    (username != null ? '&username=' + encodeURIComponent(username) : '') +
    (color != null ? '&color=' + Color[color] : '') +
    (platform != null ? '&platform=' + Platform[platform] : '') +
    (timeControl != null ? '&timeControl=' + encodeURIComponent(timeControl) : '') +
    (from != null ? '&from=' + from : '') +
    (to != null ? '&to=' + to : '') +
    (group != null ? '&group=' + encodeURIComponent(group) : '');
  const response = await fetch(url);
  return await getJson<GameMoveModel[]>(response);
}

async function evaluate(
  gameId: number,
  strength?: EvaluationStrength,
): Promise<EvaluationResult> {
  const query = `gameId=${gameId}${strength ? `&strength=${strength}` : ''}`;
  const response = await fetch('/api/Games/evaluate?' + query, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
  // The backend returns an EvaluationResult body even on failure (HTTP 500) with a
  // human-readable `Error`. Read it directly so the UI can explain what went wrong
  // instead of surfacing a bare status code.
  let result: EvaluationResult | null = null;
  try {
    const text = await response.text();
    result = text === '' ? null : (JSON.parse(text) as EvaluationResult);
  } catch {
    result = null;
  }
  if (!response.ok || result?.Error) {
    throw new Error(result?.Error || `${response.status} ${response.statusText}`);
  }
  return result as EvaluationResult;
}

async function deleteGame(gameId: number): Promise<void> {
  const response = await fetch('/api/Games/delete', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ gameId: gameId.toString() }),
  });
  await assertResponse(response);
}

// Bulk delete — backs "Delete" in the list's selection mode. The server drops any id in a group
// the caller can't manage.
async function deleteMany(gameIds: number[]): Promise<void> {
  const response = await fetch('/api/Games/deleteMany', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ GameIds: gameIds }),
  });
  await assertResponse(response);
}

async function getLatestGame(username: string, platform: Platform): Promise<GameModel> {
  const url = `/api/Games/latest_game?username=${username}&platform=${Platform[platform]}`;
  const response = await fetch(url);
  return await getJson<GameModel>(response);
}

// Every game id matching the current filter, across all pages — for "select all", and for a repair
// session, which practises the whole filtered set rather than the page on screen. `evaluatedOnly`
// drops games the engine hasn't looked at: they can't contribute a mistake to repair.
async function getGameIds(
  params: GamesRequestParams,
  evaluatedOnly = false,
): Promise<number[]> {
  const qs = getGamesUrl(params);
  const url =
    '/api/Games/ids' + qs + (evaluatedOnly ? `${qs.includes('?') ? '&' : '?'}evaluatedOnly=true` : '');
  const response = await fetch(url);
  return (await getJson<number[]>(response)) ?? [];
}

export default {
  evaluate,
  deleteGame,
  deleteMany,
  getGames,
  getGameIds,
  getById,
  getFilters,
  getAlternatives,
  getLatestGame,
  updateUserGame,
};
